import { useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { debounce } from 'lodash-es';

import {
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Skeleton,
  Stack,
  Tag,
  TagLabel,
  Text,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';

import { CardCarousel, CardCarouselSlide } from '~/components/carousel/card-carousel';
import { ContentContainer } from '~/components/container';
import { Modal } from '~/components/modal';
import {
  WoolfProgram,
  WoolfProgramCard,
  WoolfProgramCardSkeleton,
} from '~/features/accreditation/components/woolf-program-card';
import {
  accreditationColorHex,
  AccreditationColorScheme,
  AccreditationMilestonesProps,
} from '~/features/accreditation/types';
import { trackEvent } from '~/features/analytics/track-event';
import { useCatalogItems } from '~/features/catalog/hooks/use-catalog-items';
import { CatalogItem } from '~/features/catalog/models/catalog-item';
import { FormalDegreeKey } from '~/features/checkout/constants';
import { SectionHeader } from '~/features/experiments/ab_plp_redesign_2025/components/section-header';
import {
  CheckIcon,
  Difficulty1Icon,
  Difficulty2Icon,
  Difficulty3Icon,
  EyeIcon,
  ListIcon,
  StarIcon,
  StopWatchIcon,
} from '~/features/experiments/ab_plp_redesign_2025/icons';
import { useCourseLink } from '~/features/program/hooks/use-course-link';
import { getDifficultyLevel } from '~/features/program/utils/get-difficulty-data';
import { getRawDuration } from '~/features/program/utils/get-raw-duration';
import { useI18n } from '~/hooks/use-i18n';

const woolfCardColors: Record<AccreditationColorScheme, { accentColor: string; imageOverlayColor: string }> = {
  blue: { accentColor: 'gold.500', imageOverlayColor: '#EDA93B' },
  crimson: { accentColor: 'crimson.500', imageOverlayColor: '#F4737F' },
};

export function AccreditationMilestonesSection({
  colorScheme = 'blue',
  sectionTitle,
  electiveKeys,
  electiveCreditHours,
  capstoneCreditHours,
  coreDescription,
  capstoneDescription,
  electiveDescription,
  modalAlertText,
  corePrograms,
  woolfToUdacityProgramKeys,
  formalDegreeKey,
}: { colorScheme?: AccreditationColorScheme; formalDegreeKey?: FormalDegreeKey } & AccreditationMilestonesProps) {
  const { accentColor, imageOverlayColor } = woolfCardColors[colorScheme];

  const allRelatedUdacityKeys = useMemo(
    () => [...new Set(Object.values(woolfToUdacityProgramKeys).flat())],
    [woolfToUdacityProgramKeys]
  );

  const { catalogItems: relatedUdacityCatalogItems = [], catalogItemsIsLoading: relatedUdacityItemsIsLoading } =
    useCatalogItems(allRelatedUdacityKeys);
  const { catalogItems: electiveCatalogItems = [], catalogItemsIsLoading: electiveCatalogItemsIsLoading } =
    useCatalogItems(electiveKeys);

  const relatedUdacityItemsByKey = useMemo(() => {
    const map: Record<string, CatalogItem> = {};
    for (const item of relatedUdacityCatalogItems) {
      map[item.key] = item;
    }
    return map;
  }, [relatedUdacityCatalogItems]);

  const coreProgramsWithImages = useMemo(() => {
    return corePrograms.map((program) => {
      if (program.image) return program;
      const relatedKeys = woolfToUdacityProgramKeys[program.key] ?? [];
      const firstRelated = relatedKeys.length > 0 ? relatedUdacityItemsByKey[relatedKeys[0]] : undefined;
      if (firstRelated?.image) {
        return { ...program, image: firstRelated.image };
      }
      return program;
    });
  }, [corePrograms, woolfToUdacityProgramKeys, relatedUdacityItemsByKey]);

  function getRelatedUdacityPrograms(woolfKey: string): CatalogItem[] {
    const keys = woolfToUdacityProgramKeys[woolfKey] ?? [];
    return keys.map((k) => relatedUdacityItemsByKey[k]).filter(Boolean);
  }

  return (
    <Flex as='section' flexDirection='column' gap='56px'>
      <ContentContainer contentMaxWidth='content-max-width-md'>
        <SectionHeader
          color='grey.500'
          containerProps={{ maxWidth: '900px', margin: '0 auto' }}
          iconSlot={<ListIcon color='grey.500' height='18px' width='18px' />}
          subtitle='inside the program'
          descriptionSlot={
            <Text color='grey.500'>
              Your path to graduation combines three key components. Together, they ensure you gain breadth, depth, and
              proof of your expertise.
            </Text>
          }
          titleSlot={
            <Heading
              as='h2'
              fontWeight='600'
              lineHeight='1.1'
              fontSize={{
                base: '2.5rem',
                md: '3.375rem',
              }}
            >
              {sectionTitle}
            </Heading>
          }
        />

        <Flex flexDirection='column' gap={{ base: '24px', md: '32px' }} mt={{ base: '40px', md: '72px' }}>
          <MilestoneHeader
            badge='Required'
            colorScheme={colorScheme}
            description={coreDescription}
            number={1}
            title='Core Modules'
          />

          <Box display={{ base: 'none', xl: 'block' }}>
            <ProgramCarousel
              accentColor={accentColor}
              colorScheme={colorScheme}
              formalDegreeKey={formalDegreeKey}
              getRelatedUdacityPrograms={getRelatedUdacityPrograms}
              imageOverlayColor={imageOverlayColor}
              isLoading={relatedUdacityItemsIsLoading}
              modalAlertText={modalAlertText}
              programs={coreProgramsWithImages}
            />
          </Box>

          <Box display={{ base: 'block', xl: 'none' }}>
            <ProgramList
              accentColor={accentColor}
              colorScheme={colorScheme}
              formalDegreeKey={formalDegreeKey}
              getRelatedUdacityPrograms={getRelatedUdacityPrograms}
              imageOverlayColor={imageOverlayColor}
              isLoading={relatedUdacityItemsIsLoading}
              modalAlertText={modalAlertText}
              programs={coreProgramsWithImages}
            />
          </Box>
        </Flex>

        <Flex flexDirection='column' gap={{ base: '24px', md: '32px' }} mt={{ base: '40px', md: '72px' }}>
          <MilestoneHeader
            badge={`${electiveCreditHours} hours`}
            colorScheme={colorScheme}
            description={electiveDescription}
            number={2}
            title='Elective Modules'
          />
          <CondensedCatalogCardGrid
            catalogItems={electiveCatalogItems}
            colorScheme={colorScheme}
            formalDegreeKey={formalDegreeKey}
            isLoading={electiveCatalogItemsIsLoading}
          />
        </Flex>

        <Flex flexDirection='column' gap={{ base: '24px', md: '32px' }} mt={{ base: '40px', md: '72px' }}>
          <MilestoneHeader
            badge={capstoneCreditHours !== undefined ? `${capstoneCreditHours} hours` : undefined}
            colorScheme={colorScheme}
            description={capstoneDescription}
            number={3}
            title='Capstone'
          />
        </Flex>
      </ContentContainer>
    </Flex>
  );
}

const milestoneAccentColors: Record<AccreditationColorScheme, { accent: string; divider: string }> = {
  blue: { accent: 'gold.500', divider: 'rgba(145, 91, 20, .32)' },
  crimson: { accent: 'crimson.500', divider: 'rgba(156, 13, 34, .32)' },
};

export function MilestoneHeader({
  colorScheme = 'blue',
  number,
  title,
  description,
  badge,
}: {
  colorScheme?: AccreditationColorScheme;
  number: number;
  title: string;
  description: string;
  badge?: string;
}) {
  const { accent, divider } = milestoneAccentColors[colorScheme];

  return (
    <Flex
      bg='white'
      border='solid 1px'
      borderColor={accent}
      borderRadius='8px'
      flexDirection='column'
      gap='16px'
      p={{ base: '16px', md: '24px' }}
    >
      <Flex alignItems='center' gap='16px'>
        <Flex
          alignItems='center'
          bg={accent}
          borderRadius='1.71px'
          color='white'
          fontSize={{ base: '0.875rem', md: '1rem' }}
          h={{ base: '24px', md: '26px' }}
          justifyContent='center'
          w={{ base: '24px', md: '26px' }}
        >
          {number}
        </Flex>
        <Heading as='h3' color={accent} fontWeight='600' size={{ base: 'mobile-heading-5', md: 'heading-6' }}>
          {title}
        </Heading>
        {badge && (
          <Flex
            alignItems='center'
            bg='grey.100'
            border='solid 1px'
            borderColor='grey.200'
            borderRadius='4px'
            display={{ base: 'none', md: 'flex' }}
            fontFamily='condensed'
            fontSize='0.8125rem'
            h='25px'
            letterSpacing='wider'
            lineHeight='1'
            px='8px'
            textTransform='uppercase'
          >
            {badge}
          </Flex>
        )}
      </Flex>
      <Box bg={divider} h='1px' w='100%' />
      <Text color='grey.500' fontSize='sm'>
        {description}
      </Text>
      {badge && (
        <Flex
          alignItems='center'
          bg='grey.100'
          border='solid 1px'
          borderColor='grey.200'
          borderRadius='4px'
          display={{ base: 'flex', md: 'none' }}
          fontFamily='condensed'
          fontSize='0.8125rem'
          h='25px'
          justifyContent='center'
          letterSpacing='wider'
          lineHeight='1'
          px='8px'
          textTransform='uppercase'
          width='fit-content'
        >
          {badge}
        </Flex>
      )}
    </Flex>
  );
}

function ProgramCarousel({
  programs,
  colorScheme = 'blue',
  isLoading = false,
  getRelatedUdacityPrograms,
  modalAlertText,
  accentColor,
  imageOverlayColor,
  formalDegreeKey,
}: {
  programs: WoolfProgram[];
  colorScheme?: AccreditationColorScheme;
  isLoading?: boolean;
  getRelatedUdacityPrograms: (woolfKey: string) => CatalogItem[];
  modalAlertText?: string;
  accentColor: string;
  imageOverlayColor: string;
  formalDegreeKey?: FormalDegreeKey;
}) {
  const isCrimson = colorScheme === 'crimson';
  const navButtonColor = isCrimson ? 'crimson.500' : undefined;
  const isMobile = useBreakpointValue(
    {
      base: true,
      md: false,
    },
    { fallback: 'md' }
  );
  const handleSlideMove = debounce(() => {
    trackEvent({
      name: 'Masters Page CTA Clicked',
      type: 'click',
      label: 'core_ND_scrolled',
      additionalAttributes: {
        formal_degree_key: formalDegreeKey,
      },
    });
  }, 100);

  const slideWidth = isMobile ? '170px' : '256px';

  const slides: CardCarouselSlide[] = isLoading
    ? Array.from({ length: 4 }).map((_, index) => ({
        key: `skeleton-${index}`,
        slideProps: { style: { width: slideWidth } },
        content: <WoolfProgramCardSkeleton width='100%' />,
      }))
    : programs.map((program, index) => ({
        key: program.key + index,
        slideProps: { style: { width: slideWidth } },
        content: (
          <WoolfProgramCard
            accentColor={accentColor}
            imageOverlayColor={imageOverlayColor}
            modalAlertText={modalAlertText}
            program={program}
            relatedUdacityPrograms={getRelatedUdacityPrograms(program.key)}
            width='100%'
            onClick={() =>
              trackEvent({
                name: 'Masters Page CTA Clicked',
                type: 'click',
                label: 'core_ND_clicked',
                value: program.title ?? '',
                additionalAttributes: {
                  formal_degree_key: formalDegreeKey,
                },
              })
            }
          />
        ),
      }));

  return (
    <CardCarousel
      navButtonActiveBg={isCrimson ? 'crimson.100' : undefined}
      navButtonColor={navButtonColor}
      navButtonHoverBg={isCrimson ? 'crimson.50' : undefined}
      navButtonOpacity={isCrimson ? 0.6 : 0.5}
      slides={slides}
      onSlideMove={handleSlideMove}
      onSlideNext={handleSlideMove}
      onSlidePrev={handleSlideMove}
    />
  );
}

function ProgramList({
  programs,
  colorScheme = 'blue',
  isLoading = false,
  getRelatedUdacityPrograms,
  modalAlertText,
  accentColor,
  imageOverlayColor,
  formalDegreeKey,
}: {
  programs: WoolfProgram[];
  colorScheme?: AccreditationColorScheme;
  isLoading?: boolean;
  getRelatedUdacityPrograms: (woolfKey: string) => CatalogItem[];
  modalAlertText?: string;
  accentColor: string;
  imageOverlayColor: string;
  formalDegreeKey?: FormalDegreeKey;
}) {
  const MAX_PROGRAM_CARDS = 4;
  const [showAll, setShowAll] = useState(false);
  const visiblePrograms = showAll ? programs : programs.slice(0, MAX_PROGRAM_CARDS);

  function handleShowAllClick() {
    trackEvent({
      name: 'Masters Page CTA Clicked',
      type: 'click',
      label: showAll ? 'show_less_programs_clicked' : 'show_all_programs_clicked',
      additionalAttributes: {
        formal_degree_key: formalDegreeKey,
      },
    });
    setShowAll((prev) => !prev);
  }

  return (
    <Flex flexDirection='column' gap='24px'>
      <SimpleGrid columns={2} gap='16px'>
        {isLoading
          ? Array.from({ length: MAX_PROGRAM_CARDS }).map((_, index) => (
              <WoolfProgramCardSkeleton key={`skeleton-${index}`} width='100%' />
            ))
          : visiblePrograms.map((program) => (
              <WoolfProgramCard
                key={program.key}
                accentColor={accentColor}
                imageOverlayColor={imageOverlayColor}
                modalAlertText={modalAlertText}
                program={program}
                relatedUdacityPrograms={getRelatedUdacityPrograms(program.key)}
                onClick={() =>
                  trackEvent({
                    name: 'Masters Page CTA Clicked',
                    type: 'click',
                    label: 'core_ND_clicked',
                    value: program.title ?? '',
                    additionalAttributes: {
                      formal_degree_key: formalDegreeKey,
                    },
                  })
                }
              />
            ))}
      </SimpleGrid>

      {programs.length > MAX_PROGRAM_CARDS && (
        <Button
          alignSelf='center'
          borderColor={`${colorScheme}.700`}
          boxShadow={`inset 0 0 0 1.5px ${accreditationColorHex[colorScheme][700]}`}
          color={`${colorScheme}.700`}
          fontWeight='600'
          gap='8px'
          variant='outline'
          width={{
            base: '100%',
            md: 'fit-content',
          }}
          onClick={handleShowAllClick}
        >
          <EyeIcon h='18px' w='18px' />
          {showAll ? 'Show less programs' : 'Show all programs'}
        </Button>
      )}
    </Flex>
  );
}

function CondensedCatalogCardGrid({
  catalogItems,
  colorScheme = 'blue',
  isLoading = false,
  formalDegreeKey,
}: {
  catalogItems: CatalogItem[];
  colorScheme?: AccreditationColorScheme;
  isLoading?: boolean;
  formalDegreeKey?: FormalDegreeKey;
}) {
  const MAX_PROGRAM_CARDS = 9;
  const visibleCatalogItems = catalogItems.slice(0, MAX_PROGRAM_CARDS);

  return (
    <Flex flexDirection='column' gap={{ base: '24px', md: '32px' }}>
      <SimpleGrid columns={[1, 1, 1, 3]} gap={{ base: '8px', md: '16px' }}>
        {isLoading
          ? Array.from({ length: MAX_PROGRAM_CARDS }).map((_, index) => (
              <Skeleton key={index} borderRadius='8px' height='103px' width='100%' />
            ))
          : visibleCatalogItems.map((catalogItem) => (
              <CondensedCatalogCard key={catalogItem.key} catalogItem={catalogItem} showDuration={false} />
            ))}
      </SimpleGrid>
      {catalogItems.length > MAX_PROGRAM_CARDS && (
        <AllElectivesModalButton allElectives={catalogItems} formalDegreeKey={formalDegreeKey} />
      )}
    </Flex>
  );
}

function CondensedCatalogCard({
  catalogItem,
  showDuration = true,
}: {
  catalogItem: CatalogItem;
  showDuration?: boolean;
}) {
  const { t } = useI18n();

  const schoolTheme = useMemo(() => {
    switch (catalogItem.schools[0]?.uri) {
      // Artificial Intelligence
      case 'taxonomy:b8b37a22-80bb-45fe-8c41-7d7179cc306d':
        return {
          primaryColor: 'blue.500',
          fontColor: 'blue.500',
          iconPath: '/images/school-icons/artificial-intelligence.svg',
        };
      // Data Science
      case 'taxonomy:0797c703-1d76-487a-9219-b84cf74b66a5':
        return {
          primaryColor: 'blue.500',
          fontColor: 'blue.500',
          iconPath: '/images/school-icons/data-science.svg',
        };
      // Programming and Development
      case 'taxonomy:d5b00600-d85f-4c4a-a493-e7ed9374be89':
        return {
          primaryColor: 'cyan.500',
          fontColor: 'cyan.700',
          iconPath: '/images/school-icons/programming.svg',
        };
      // Cloud Computing
      case 'taxonomy:37efa4cd-9fb8-48ed-a3be-4fe488e3704f':
        return {
          primaryColor: 'cyan.500',
          fontColor: 'cyan.700',
          iconPath: '/images/school-icons/cloud-computing.svg',
        };
      // Autonomous Systems
      case 'taxonomy:c7075247-28e1-4047-9a11-d86f2a3143d5':
        return {
          primaryColor: 'orange.500',
          fontColor: 'orange.700',
          iconPath: '/images/school-icons/autonomous-systems.svg',
        };
      // Cybersecurity
      case 'taxonomy:b4a305f0-00bb-4c2c-bcfd-e875edcdcd61':
        return {
          primaryColor: 'orange.500',
          fontColor: 'orange.700',
          iconPath: '/images/school-icons/cybersecurity.svg',
        };
      // Product Management
      case 'taxonomy:0dba323e-c4c6-4d09-bf98-365cf420a83a':
        return {
          primaryColor: 'purple.500',
          fontColor: 'purple.500',
          iconPath: '/images/school-icons/product-management.svg',
        };
      // Business
      case 'taxonomy:63c3498c-9e8b-4d85-bcd4-2843d519ee04':
        return {
          primaryColor: 'purple.500',
          fontColor: 'purple.500',
          iconPath: '/images/school-icons/business.svg',
        };
      // Executive Leadership
      case 'taxonomy:1ab68afb-1a62-434e-bffd-7d0f18975299':
        return {
          primaryColor: 'purple.500',
          fontColor: 'purple.500',
          iconPath: '/images/school-icons/executive-leadership.svg',
        };
      // Career Resources
      case 'taxonomy:c16ea252-b5e1-4b2b-b56c-f953e4eff453':
        return {
          primaryColor: 'purple.500',
          fontColor: 'purple.500',
          iconPath: '/images/school-icons/career-resources.svg',
        };
      default:
        return {
          primaryColor: 'blue.500',
          fontColor: 'blue.500',
          iconPath: null,
        };
    }
  }, [catalogItem.schools]);

  const localeDifficultyLevel = getDifficultyLevel(catalogItem.difficultyLevel, t);
  const localeRawDuration = catalogItem.rawDurationDisplay ? getRawDuration(catalogItem.rawDurationDisplay, t) : null;

  const courseLink = useCourseLink({ courseSlug: catalogItem.slug });

  return (
    <Flex
      as={Link}
      bg='white'
      border='solid 1px'
      borderColor='grey.100'
      borderRadius='8px'
      flexDirection='column'
      gap='12px'
      href={courseLink}
      p='16px'
    >
      <Flex alignItems='center' flex={1} gap='16px'>
        {schoolTheme.iconPath && (
          <>
            <Box display={{ base: 'none', md: 'block' }} flexShrink={0}>
              <Image
                alt={catalogItem.schools[0]?.name ?? ''}
                height={28}
                quality={100}
                src={schoolTheme.iconPath}
                width={28}
              />
            </Box>
            <Box display={{ base: 'block', md: 'none' }}>
              <Image
                alt={catalogItem.schools[0]?.name ?? ''}
                height={24}
                quality={100}
                src={schoolTheme.iconPath}
                width={24}
              />
            </Box>
          </>
        )}
        <Text fontSize={{ base: '0.875rem', md: '1rem' }} fontWeight='600' noOfLines={1} wordBreak='break-all'>
          {catalogItem.title}
        </Text>
      </Flex>

      <Box bg='grey.100' h='1px' w='full' />

      <Flex alignItems='center' justifyContent='space-between'>
        <Flex alignItems='center' color='grey.500' gap={1}>
          {catalogItem.difficultyLevel && (
            <>
              {(catalogItem.difficultyLevel === 'Beginner' ||
                catalogItem.difficultyLevel === 'Discovery' ||
                catalogItem.difficultyLevel === 'Fluency') && (
                <Difficulty1Icon
                  height={{
                    base: '12px',
                    md: '16px',
                  }}
                  width={{
                    base: '12px',
                    md: '16px',
                  }}
                />
              )}
              {catalogItem.difficultyLevel === 'Intermediate' && (
                <Difficulty2Icon
                  height={{
                    base: '12px',
                    md: '16px',
                  }}
                  width={{
                    base: '12px',
                    md: '16px',
                  }}
                />
              )}
              {catalogItem.difficultyLevel === 'Advanced' && <Difficulty3Icon h='16px' w='16px' />}
              <Text fontSize='0.75rem' noOfLines={1} wordBreak='break-all'>
                {localeDifficultyLevel}
              </Text>
            </>
          )}
        </Flex>
        <Flex alignItems='center' color='grey.500' gap='32px'>
          <Flex alignItems='center' gap='6px'>
            {localeRawDuration && showDuration && (
              <>
                <StopWatchIcon h='16px' w='16px' />
                <Text fontSize='0.75rem' noOfLines={1} textTransform='lowercase' wordBreak='break-all'>
                  {localeRawDuration}
                </Text>
              </>
            )}
          </Flex>
          <Flex alignItems='center' gap='6px'>
            {!!catalogItem.reviewSummary.starsAverage && (
              <Flex alignItems='center' gap='4px'>
                <StarIcon color='orange.500' h='16px' w='16px' />
                <Text color='orange.700' fontSize='0.75rem' fontWeight='500'>
                  {catalogItem.reviewSummary.starsAverage}
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}

function AllElectivesModalButton({
  allElectives,
  formalDegreeKey,
}: {
  allElectives: CatalogItem[];
  formalDegreeKey?: FormalDegreeKey;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure({
    onOpen: () =>
      trackEvent({
        name: 'Masters Page CTA Clicked',
        type: 'click',
        label: 'show_all_programs_clicked',
        additionalAttributes: {
          formal_degree_key: formalDegreeKey,
        },
      }),
  });
  const electiveProgramsBySchool = useMemo(() => {
    return allElectives.reduce(
      (acc, nextProgram) => {
        return {
          ...acc,
          [nextProgram.schools[0]?.name]: [...(acc[nextProgram.schools[0]?.name] || []), nextProgram],
        };
      },
      {} as Record<string, CatalogItem[]>
    );
  }, [allElectives]);
  return (
    <>
      <Button
        alignSelf='center'
        borderColor='blue.700'
        boxShadow={`inset 0 0 0 1.5px ${accreditationColorHex.blue[700]}`}
        color='blue.700'
        display='flex'
        fontWeight='600'
        gap='8px'
        variant='outline'
        width={{
          base: '100%',
          md: 'fit-content',
        }}
        onClick={onOpen}
      >
        <EyeIcon h='18px' w='18px' />
        Show All Elective Courses
      </Button>
      <Modal
        isOpen={isOpen}
        size='3xl'
        title={
          <Heading as='header' size='heading-5'>
            All Elective Courses
          </Heading>
        }
        onClose={onClose}
      >
        <Stack spacing={8}>
          {Object.entries(electiveProgramsBySchool).map(([schoolName, programs]) => (
            <Flex key={schoolName} flexDirection='column' gap={2}>
              <Flex alignItems='center' gap={2}>
                <CheckIcon color='primary.blue' height='32px' width='32px' />
                <Heading as='h3' size='heading-7'>
                  School of{' '}
                  <Box as='span' textTransform='capitalize'>
                    {schoolName}
                  </Box>
                </Heading>
              </Flex>
              <Flex flexWrap='wrap' gap={2}>
                {programs.map((program) => (
                  <ProgramTag key={program.key} program={program} />
                ))}
              </Flex>
            </Flex>
          ))}
        </Stack>
      </Modal>
    </>
  );
}

function ProgramTag({ program }: { program: CatalogItem }) {
  const courseLink = useCourseLink({ courseSlug: program.slug });
  return (
    <Tag
      key={program.key}
      as={Link}
      href={courseLink}
      size='lg'
      textDecoration='underline'
      _active={{
        color: 'primary.blue',
      }}
      _focus={{
        color: 'primary.blue',
        outline: '2px solid',
      }}
    >
      <TagLabel as='span'>{program.title}</TagLabel>
    </Tag>
  );
}
